import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { formatDate, formatDateTime, formatSolutionType } from '../lib/formatters';
import type { AdminTicket } from '../hooks/useAllTickets';

function useAdminTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['admin-ticket', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          id, ticket_number, customer_id, location_id, deployment_id, asset_id,
          title, description, status, priority, source, sla_deadline,
          resolution_notes, resolved_at, created_at, updated_at,
          customer:customers(id, name),
          location:locations(id, name),
          deployment:deployments(id, solution_type),
          assigned_to:users!tickets_assigned_to_user_id_fkey(id, full_name, email)
        `)
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as AdminTicket | null;
    },
  });
}

export function TicketDetailPage() {
  const { id, ticketId } = useParams<{ id?: string; ticketId?: string }>();
  const ticketKey = ticketId ?? id;
  const ticketsHref = ticketId && id ? `/locations/${id}/tickets` : '/tickets';
  const navigate = useNavigate();
  const { data: ticket, isLoading, isError } = useAdminTicket(ticketKey);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center gap-3 text-sm text-text-muted">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        Loading ticket…
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <EmptyState
        icon={<TicketIcon />}
        title="Ticket not found"
        subtitle="It may have been removed or you may not have access."
        action={{ label: 'Back to tickets', onClick: () => navigate(ticketsHref) }}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <button type="button" onClick={() => navigate(ticketsHref)} className="text-sm text-primary hover:underline">
          ← Back to tickets
        </button>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-mono text-text-faint">{ticket.ticket_number}</p>
            <h1 className="break-words text-2xl font-bold text-text">{ticket.title}</h1>
          </div>
          <div className="flex gap-2">
            <StatusBadge status={ticket.priority ?? 'medium'} />
            <StatusBadge status={ticket.status ?? 'open'} />
          </div>
        </div>
      </div>

      <section className="rounded-lg border border-border bg-surface p-5">
        <h2 className="font-semibold text-text">Customer and site</h2>
        <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Fact label="Customer">
            {ticket.customer ? (
              <Link to={`/customers/${ticket.customer.id}`} className="text-primary hover:underline">
                {ticket.customer.name}
              </Link>
            ) : 'Unmapped'}
          </Fact>
          <Fact label="Site">
            {ticket.location ? (
              <Link to={`/locations/${ticket.location.id}`} className="text-primary hover:underline">
                {ticket.location.name}
              </Link>
            ) : 'Not assigned'}
          </Fact>
          <Fact label="Deployment">
            {ticket.deployment ? formatSolutionType(ticket.deployment.solution_type) : 'Not assigned'}
          </Fact>
          <Fact label="Assigned to">
            {ticket.assigned_to?.full_name ?? ticket.assigned_to?.email ?? 'Unassigned'}
          </Fact>
        </dl>
      </section>

      <section className="rounded-lg border border-border bg-surface p-5">
        <h2 className="font-semibold text-text">Description</h2>
        <p className="mt-3 whitespace-pre-wrap text-sm text-text">
          {ticket.description || 'No description provided.'}
        </p>
      </section>

      <section className="rounded-lg border border-border bg-surface p-5">
        <h2 className="font-semibold text-text">Ticket details</h2>
        <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Fact label="Source">{formatLabel(ticket.source)}</Fact>
          <Fact label="Created">{formatDateTime(ticket.created_at)}</Fact>
          <Fact label="Updated">{formatDateTime(ticket.updated_at)}</Fact>
          <Fact label="SLA deadline">{ticket.sla_deadline ? formatDateTime(ticket.sla_deadline) : '—'}</Fact>
          <Fact label="Resolved">{ticket.resolved_at ? formatDate(ticket.resolved_at) : '—'}</Fact>
        </dl>
      </section>

      {ticket.resolution_notes && (
        <section className="rounded-lg border border-success/30 bg-success-soft/30 p-5">
          <h2 className="font-semibold text-success">Resolution</h2>
          <p className="mt-3 whitespace-pre-wrap text-sm text-text">{ticket.resolution_notes}</p>
        </section>
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-right text-text">{children}</dd>
    </div>
  );
}

function formatLabel(value: string | null): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function TicketIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
    </svg>
  );
}
